import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  BarChart,
  Button,
  InlineAlert,
  LineChart,
  List,
  Metric,
  Panel,
  Progress,
  cn,
  PRESS,
  formatDate,
  formatMoney,
} from 'escrowbuild-ui';

import { useDocuments, useFixtureVocabulary, usePublications, useT, useUpdates } from '@/i18n';
import { EXPENSES, PROJECTS, findProject, projectTotals, scheduleOf } from '@/data/domain';

/**
 * The portfolio landing screen.
 *
 * Every figure on this page is summed from `PROJECTS` through `projectTotals`
 * rather than restated here. A dashboard is the one screen a client checks a
 * project card against, so a total typed in by hand would eventually disagree
 * with the card two clicks away — and the whole demo would read as unreliable.
 */
export function DashboardPage() {
  const t = useT();
  const [alertDismissed, setAlertDismissed] = useState(false);

  /*
     Three of the four lists below name a record in their own words — a
     publication's title and period, a document's name, an update's title and
     body — so they are resolved for the active locale. The projects and the
     expense ledger stay as `domain.ts` exports them: what this screen takes from
     those is a street address, a supplier's name and a sum, and none of the
     three is a word the reader is being told.
  */
  const documents = useDocuments();
  const publications = usePublications();
  const updates = useUpdates();
  const vocabulary = useFixtureVocabulary();

  const portfolio = useMemo(() => {
    const rows = PROJECTS.map((project) => ({
      project,
      totals: projectTotals(project.id),
      activePhase: scheduleOf(project.id).find((phase) => phase.status === 'in-progress'),
    }));

    return {
      rows,
      /*
         Promised and paid are different sums — 2,570,000 against 2,510,000 on
         this data — so they are separate fields. The previous version kept the
         contributed sum under a `committed` name, which labelled the tile with
         the wrong noun: a client who adds up the participations finds 2,570,000
         and concludes the dashboard is wrong.
      */
      committed: rows.reduce((sum, row) => sum + row.totals.committed, 0),
      contributed: rows.reduce((sum, row) => sum + row.totals.contributed, 0),
      spent: rows.reduce((sum, row) => sum + row.totals.actual, 0),
      unspent: rows.reduce((sum, row) => sum + row.totals.available, 0),
      variance: rows.reduce((sum, row) => sum + row.totals.variance, 0),
      /*
         Work-started sums, which are the ones the headline variance reads.
         Against the full phased budget the whole portfolio always looks far
         under budget, because most of that budget is for phases nobody has
         ordered yet. This is the comparable figure.
      */
      startedBudgeted: rows.reduce((sum, row) => sum + row.totals.startedBudgeted, 0),
      varianceStarted: rows.reduce((sum, row) => sum + row.totals.varianceStarted, 0),
      active: rows.filter((row) => row.activePhase).length,
    };
  }, []);

  /*
     §11's gate, read backwards: these are the records that exist but cannot be
     shown to an investor yet, each one named by its project so the reader knows
     where to go. Deriving the list from the fixtures rather than writing it out
     means this screen cannot claim the queue is clear while it is not.
  */
  const attention = useMemo(() => {
    const items: { id: string; text: string }[] = [];

    publications.filter((entry) => entry.status === 'draft' || entry.status === 'in-review').forEach(
      (entry) => {
        const project = findProject(entry.projectId);
        items.push({
          id: entry.id,
          text: t(
            entry.status === 'draft' ? 'dashboard.attention.draft' : 'dashboard.attention.inReview',
            {
              project: project?.name ?? entry.projectId,
              title: entry.title,
              period: entry.period,
            },
          ),
        });
      },
    );

    EXPENSES.filter((entry) => entry.status === 'in-review').forEach((entry) => {
      const project = findProject(entry.projectId);
      items.push({
        id: entry.id,
        text: t('dashboard.attention.expense', {
          project: project?.name ?? entry.projectId,
          vendor: entry.vendorName,
          amount: formatMoney(entry.amount, { precision: 0 }),
        }),
      });
    });

    documents.filter((entry) => entry.status === 'rejected').forEach((entry) => {
      const project = findProject(entry.projectId);
      items.push({
        id: entry.id,
        text: t('dashboard.attention.document', {
          project: project?.name ?? entry.projectId,
          name: entry.name,
        }),
      });
    });

    return items;
  }, [t, documents, publications]);

  const recentUpdates = useMemo(
    () => [...updates].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 5),
    [updates],
  );

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="font-display text-3xl font-semibold uppercase tracking-title text-heading">
          {t('dashboard.title')}
        </h1>
        <p className="mt-1 text-sm text-muted">
          {t('dashboard.subtitle', { count: PROJECTS.length })}
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Metric
          label={t('dashboard.committed')}
          value={portfolio.committed}
          format="money"
          precision={0}
          hint={t('dashboard.committedHint', {
            amount: formatMoney(portfolio.contributed, { precision: 0 }),
          })}
        />
        <Metric
          label={t('dashboard.totalSpent')}
          value={portfolio.spent}
          format="money"
          precision={0}
        />
        <Metric
          label={t('dashboard.unspent')}
          value={portfolio.unspent}
          format="money"
          precision={0}
          tone="info"
          hint={t('dashboard.unspentHint')}
        />
        <Metric
          label={t('project.shell.variance')}
          value={portfolio.varianceStarted}
          format="money"
          precision={0}
          tone={portfolio.varianceStarted <= 0 ? 'success' : 'danger'}
          // Share of the started budget, not the variance repeated — one number
          // shown twice reads as two measurements agreeing.
          delta={
            portfolio.startedBudgeted === 0
              ? 0
              : (portfolio.varianceStarted / portfolio.startedBudgeted) * 100
          }
          deltaFormat="percentage"
          trend={portfolio.varianceStarted <= 0 ? 'down' : 'up'}
          hint={t('dashboard.varianceHint', {
            amount: formatMoney(portfolio.startedBudgeted),
          })}
        />
        <Metric
          label={t('dashboard.active')}
          value={portfolio.active}
          format="number"
          hint={t('dashboard.activeHint')}
        />
      </div>

      {attention.length > 0 ? (
        alertDismissed ? (
          <div>
            <Button variant="ghost" size="sm" onClick={() => setAlertDismissed(false)}>
              {t('dashboard.showAlert')}
            </Button>
          </div>
        ) : (
          <InlineAlert
            tone="warning"
            title={t('dashboard.attention.title', { count: attention.length })}
            dismissible
            onDismiss={() => setAlertDismissed(true)}
            description={
              <ul className="list-disc space-y-1 pl-5">
                {attention.map((item) => (
                  <li key={item.id}>{item.text}</li>
                ))}
              </ul>
            }
          />
        )
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        {portfolio.rows.map(({ project, totals, activePhase }) => (
          <Panel
            key={project.id}
            padding="md"
            title={
              <Link
                to={`/projects/${project.id}`}
                className={cn(
                  'text-heading no-underline visited:text-heading hover:text-brand hover:underline',
                  PRESS,
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                )}
              >
                {project.name}
              </Link>
            }
            description={`${project.address} · ${project.city}`}
          >
            <div className="flex flex-col gap-4">
              <div>
                <Badge tone="info" variant="subtle">
                  {vocabulary.phase(activePhase ? activePhase.id : project.phase)}
                </Badge>
              </div>

              <Progress
                value={project.progress}
                tone="primary"
                label={t('dashboard.progress')}
                showValue
              />

              <dl className="flex flex-col gap-1.5 text-sm">
                <div className="flex items-baseline justify-between gap-3">
                  {/* The row shows `totals.contributed`, so it says
                      "Contributed". It previously read "Committed" over the
                      contributed figure, which is a different number. */}
                  <dt className="text-muted">{t('project.shell.contributed')}</dt>
                  <dd className="tabular-nums text-body">
                    {formatMoney(totals.contributed, { precision: 0 })}
                  </dd>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-muted">{t('dashboard.spent')}</dt>
                  <dd className="tabular-nums text-body">
                    {formatMoney(totals.actual, { precision: 0 })}
                  </dd>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-muted">{t('dashboard.balance')}</dt>
                  <dd className="tabular-nums text-body">
                    {formatMoney(totals.available, { precision: 0 })}
                  </dd>
                </div>
              </dl>

              <p className="text-xs text-muted">
                {t('project.shell.sale', { date: formatDate(project.projectedSale) })}
              </p>
            </div>
          </Panel>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <BarChart />
        <LineChart />
      </div>

      <Panel
        padding="none"
        title={t('dashboard.updates.title')}
        description={t('dashboard.updates.description')}
      >
        {/* `List` truncates its subtitle to one line, which suits a label and not
            an update body — the sentence is the content here, so it wraps. */}
        <List
          className="[&_.truncate]:whitespace-normal"
          label={t('dashboard.updates.title')}
          divided
          items={recentUpdates.map((entry) => ({
            id: entry.id,
            title: entry.title,
            subtitle: entry.body,
            trailing: <span className="tabular-nums">{formatDate(entry.date)}</span>,
          }))}
        />
      </Panel>
    </div>
  );
}
